// Curated Thai-capable fonts for the quotation layout editor.
// Sarabun stays embedded as @font-face (PDF-safe). Others load from Google Fonts
// (or a metric-compatible substitute) so Cloudflare PDF Chromium can render them.
export const LAYOUT_FONTS = [
    { id: 'Sarabun', label: 'Sarabun', google: null },
    {
        id: 'Arial',
        label: 'Arial',
        google: 'Arimo:wght@300;400;600;700',
        stack: "Arial,Arimo,'Sarabun',sans-serif",
    },
    {
        id: 'Sukhumvit',
        label: 'Sukhumvit',
        // Sukhumvit Set is Apple-bundled (not redistributable). Prompt is the same
        // foundry's OFL webfont — used on PDF / non-Mac so the page still has a
        // loopless Thai face instead of falling all the way to Sarabun.
        google: 'Prompt:wght@300;400;600;700',
        stack: "'Sukhumvit Set','Sukhumvit Tadmai',Prompt,'Sarabun',sans-serif",
    },
    {
        id: 'Myriad Pro',
        label: 'Myriad Pro',
        // Myriad Pro is Adobe-bundled (not redistributable). Source Sans 3 is
        // Adobe's OFL humanist sans — used on PDF / machines without Myriad.
        google: 'Source+Sans+3:wght@300;400;600;700',
        stack: "'Myriad Pro','Myriad Pro Condensed','Source Sans 3','Sarabun',sans-serif",
    },
    { id: 'Prompt', label: 'Prompt', google: 'Prompt:wght@300;400;600;700' },
    { id: 'Kanit', label: 'Kanit', google: 'Kanit:wght@300;400;600;700' },
    { id: 'Noto Sans Thai', label: 'Noto Sans Thai', google: 'Noto+Sans+Thai:wght@300;400;600;700' },
    { id: 'IBM Plex Sans Thai', label: 'IBM Plex Sans Thai', google: 'IBM+Plex+Sans+Thai:wght@300;400;600;700' },
    { id: 'K2D', label: 'K2D', google: 'K2D:wght@300;400;600;700' },
    { id: 'Chakra Petch', label: 'Chakra Petch', google: 'Chakra+Petch:wght@300;400;600;700' },
    { id: 'Mali', label: 'Mali (มะลิ)', google: 'Mali:wght@300;400;600;700' },
    { id: 'Itim', label: 'Itim', google: 'Itim' },
    { id: 'Athiti', label: 'Athiti', google: 'Athiti:wght@300;400;600;700' },
];
export const LAYOUT_FONT_WEIGHTS = [
    { id: 'light', label: 'บาง', css: '300' },
    { id: 'regular', label: 'Regular', css: '400' },
    { id: 'bold', label: 'หนา', css: '700' },
];
const FONT_IDS = new Set(LAYOUT_FONTS.map((f) => f.id));
export function isLayoutFontId(value) {
    return typeof value === 'string' && FONT_IDS.has(value);
}
export function sanitizeLayoutFontId(value) {
    const raw = String(value || '').trim();
    if (!raw || raw === 'Sarabun')
        return '';
    if (/^arai$/i.test(raw) || /^arimo$/i.test(raw))
        return 'Arial';
    if (/^sukumvit$/i.test(raw) || /^sukhumvit(\s+set)?$/i.test(raw))
        return 'Sukhumvit';
    if (/^myriad(\s+pro)?$/i.test(raw))
        return 'Myriad Pro';
    if (/^marial$/i.test(raw) || /^มะลิ$/i.test(raw))
        return 'Mali';
    return isLayoutFontId(raw) ? raw : '';
}
export function sanitizeLayoutFontWeight(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'light' || raw === '300' || raw === 'thin' || raw === 'บาง')
        return 'light';
    if (raw === 'bold' || raw === '700' || raw === 'thick' || raw === 'หนา')
        return 'bold';
    return 'regular';
}
export function cssFontWeight(weightId) {
    const id = sanitizeLayoutFontWeight(weightId);
    return LAYOUT_FONT_WEIGHTS.find((w) => w.id === id)?.css || '400';
}
export function cssFontFamily(fontId) {
    const id = sanitizeLayoutFontId(fontId) || 'Sarabun';
    const opt = LAYOUT_FONTS.find((f) => f.id === id);
    if (opt?.stack)
        return opt.stack;
    return `'${id}','Sarabun',sans-serif`;
}
export function collectUsedLayoutFonts(blocks) {
    const used = new Set();
    for (const block of blocks || []) {
        const blockFont = sanitizeLayoutFontId(block.fontFamily);
        if (blockFont)
            used.add(blockFont);
        for (const value of Object.values(block.textFonts || {})) {
            const font = sanitizeLayoutFontId(value);
            if (font)
                used.add(font);
        }
    }
    return LAYOUT_FONTS.map((f) => f.id).filter((id) => used.has(id));
}
export function googleFontsCssUrl(fontIds) {
    const families = [
        ...new Set(LAYOUT_FONTS.filter((f) => f.google && fontIds.includes(f.id)).map((f) => f.google)),
    ];
    if (!families.length)
        return '';
    return 'https://fonts.googleapis.com/css2?' + families.map((f) => 'family=' + f).join('&') + '&display=swap';
}
export function googleFontsImportCss(fontIds) {
    const url = googleFontsCssUrl(fontIds);
    return url ? `@import url('${url}');` : '';
}
export function allGoogleFontsCssUrl() {
    return googleFontsCssUrl(LAYOUT_FONTS.map((f) => f.id));
}
//# sourceMappingURL=layout-fonts.js.map